/**
 * Grundfarbe der Szene — die Farbe hinter allen Ebenen (Jan 19.09.2026).
 *
 * EINE Regel fuer Editor-Buehne, Vorschau und Reader, damit alle dasselbe
 * zeigen. Vorher: Reader = `slideOptions.backgroundColor` (die Textbox-
 * Hintergrundfarbe, meist Weiss — historisch die „Leinwandfarbe" der Studio-
 * Charts), Editor = fest Schwarz. Eine halbtransparente unterste Ebene sah
 * deshalb im Editor dunkel und veroeffentlicht fast weiss aus.
 *
 * Rangfolge:
 *   1. `presentationOptions.sceneBackgroundColor` — bewusst gesetzt
 *   2. `slideOptions.backgroundColor` — was der Reader schon immer zeigte
 *      (Bestand aendert sich veroeffentlicht NICHT)
 *   3. Weiss — die Leseseite hinter der transparenten Zeichenflaeche
 * Deckkraft wird ignoriert: ein Grund ist deckend.
 */
export interface SzenenGrundfarbe {
    r: number;
    g: number;
    b: number;
    /** Woher die Farbe stammt; "szene" = im Dokument bewusst gesetzt. */
    quelle: "szene" | "textbox" | "standard";
    /** `rgb(r, g, b)` fuer CSS. */
    css: string;
    /** 0xRRGGBB fuer three. */
    hex: number;
}

type Rgb = { r: number; g: number; b: number };

const kanal = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.min(255, Math.round(v))) : null;

function rgbAus(roh: unknown): Rgb | null {
    if (!roh || typeof roh !== "object") return null;
    const q = roh as { r?: unknown; g?: unknown; b?: unknown };
    const r = kanal(q.r);
    const g = kanal(q.g);
    const b = kanal(q.b);
    return r !== null && g !== null && b !== null ? { r, g, b } : null;
}

export function resolveSzenenGrundfarbe(presentationOptions: unknown): SzenenGrundfarbe {
    // camelCase (Editor) und snake_case (Studio-Roh-JSON), wie im Player.
    const po = (presentationOptions ?? {}) as Record<string, unknown>;
    const slideOpts = (po.slideOptions ?? po.slide_options ?? {}) as Record<string, unknown>;
    const szene = rgbAus(po.sceneBackgroundColor ?? po.scene_background_color);
    const textbox = rgbAus(slideOpts.backgroundColor ?? slideOpts.background_color);
    const f: Rgb = szene ?? textbox ?? { r: 255, g: 255, b: 255 };
    return {
        ...f,
        quelle: szene ? "szene" : textbox ? "textbox" : "standard",
        css: `rgb(${f.r}, ${f.g}, ${f.b})`,
        hex: (f.r << 16) | (f.g << 8) | f.b,
    };
}
